import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { RegisterPerson } from '../models/agents/register-person';
import { Observable } from 'rxjs/Observable';
import { PersonData, Profession, Cft, Pix } from '../models/agents/person-data';
import { BankData } from '../models/agents/bank-data';
import { Files } from '../models/agents/files';
import { Address } from '../models/agents/address';
import { Authorizations, Ppe } from '../models/agents/authorizations';
import { Question } from '../models/suitability/question';
import { SuitabilityScore } from '../models/agents/suitability-score';
import { FinancialStatement } from '../models/agents/financial-statement';
import { Suitability } from '../models/suitability/suitability';
import { User } from '../models/auth/user';
import { Store } from '@ngxs/store';
import { AuthState } from '../state-manager/state/auth.state';
import { get } from 'lodash';
import { UploadStockExchangeInvestorsOutput } from '../models/agents/output/upload-stock-exchange-investors-output';
import SendNonConformityTermInput from '../models/nonConformityTerm/SendNonConformityTermInput';
import SignNonConformityTermInput from '../models/nonConformityTerm/SignNonconformityTermInput';
import { MssignService } from './mssign.service';
import { switchMap } from 'rxjs/operators';
import { InvestorFileTypes } from '../models/enums/investor-file-types.enum';
import { ContractData } from '../models/agents/contract-data';
import { Beneficiary } from '../models/agents/beneficiary';

@Injectable()
export class PersonService {
  userLogged: User;

  constructor(
    protected http: HttpClient,
    protected store: Store,
    protected msSignService: MssignService
  ) {
    setTimeout(() => {
      this.getUserLogged();
    }, 1000);
  }

  getUserLogged() {
    this.store.select(AuthState.User).subscribe((user) => {
      this.userLogged = user;
    });
  }

  registerPerson(registerPerson: RegisterPerson) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerPerson;

    return this.http.post(url, registerPerson);
  }

  updatePerson(agentId: string, person: PersonData) {
    const data = {
      agentId: agentId,
      userId: get(this.userLogged, 'userID')
    };

    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerPerson;

    return this.http.put(url, person, { params: data });
  }

  confirmRegistration(agentId: string) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personConfirmRegistration +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, {});
  }

  patchPerson(personPatchJson: any) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerPerson +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, personPatchJson);
  }

  getPersonByAgent(agentId: string): Observable<PersonData> {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personByAgent +
      '/' +
      agentId;
    return this.http.get<PersonData>(url);
  }

  updateFiles(agentId: string, files: Files[]) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personFiles +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, files);
  }

  approveResponsibleRegister(agentId: string) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.approveResponsibleRegister +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.post(url, { agentId });
  }

  reproveResponsibleRegister(agentId: string, returnReasons: string) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.reproveResponsibleRegister +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.post(url, { agentId, returnReasons });
  }

  approveRegisterAsResponsible(agentId: string) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.approveRegisterAsResponsible +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.post(url, { agentId });
  }

  investorConfirmation(agentId: string) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personInvestorConfirmation +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.post(url, { agentId });
  }

  disablePersonFile(agentId: string, fileUrl: string) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personDisableFile +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID') +
      '?fileUrl=' +
      fileUrl;
    return this.http.post(url, null);
  }

  removePersonFiles(blockReference: string) {
    const params = new HttpParams().set('blockReference', blockReference);

    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.deleteInvestorDocumentUpload;
    return this.http.delete(url, { params });
  }

  updateAddress(agentId: string, address: Address) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personAddress +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');
    return this.http.put(url, address);
  }

  updateProfission(agentId: string, profission: Profession) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personProfession +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, profission);
  }

  updateContractData(agentId: string, contractData: ContractData) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personContractData +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, contractData);
  }

  updateBeneficiariesData(agentId: string, beneficiariesData: Beneficiary[]) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personBeneficiariesData +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, beneficiariesData);
  }

  updateBankData(agentId: string, bankData: BankData[]) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personBankData +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, bankData);
  }

  updatePixData(agentId: string, pixs: Pix[]) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerPerson +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, {
      agentID: agentId,
      personPatch: [{ op: 'replace', path: '/Pixs', value: pixs }]
    });
  }

  updateAuthorizations(agentId: string, authorizations: Authorizations) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personAuthorizations +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, authorizations);
  }

  updateSuitability(agentId: string, suitability: Suitability) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerPerson +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, {
      agentID: agentId,
      personPatch: [{ op: 'replace', path: '/Suitability', value: suitability }]
    });
  }

  updatePpe(agentId: string, ppe: Ppe) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerPerson +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, {
      agentID: agentId,
      personPatch: [
        { op: 'replace', path: '/Authorizations/PPE', value: ppe.ppe },
        {
          op: 'replace',
          path: '/Authorizations/PoliticalExposedInformation',
          value: ppe.politicalExposedInformation
        }
      ]
    });
  }

  updateValidated(agentId: string, validated: boolean) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerPerson +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, {
      agentID: agentId,
      personPatch: [{ op: 'replace', path: '/Validated', value: validated }]
    });
  }

  updateCft(agentId: string, cft: Cft) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerPerson +
      '/' +
      get(this.userLogged, 'userID');

    const arrayToPatch: Array<Object> = [
      { op: 'replace', path: '/Cft', value: cft.cft }
    ];

    if (cft.cft) {
      arrayToPatch.push({
        op: 'replace',
        path: '/CftVerificationDate',
        value: cft.cftVerificationDate
      });
    }
    return this.http.patch(url, {
      agentID: agentId,
      personPatch: arrayToPatch
    });
  }

  updateCreatedAt(agentId: string, createAt: string) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerPerson +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, {
      agentID: agentId,
      personPatch: [{ op: 'replace', path: '/CreatedAt', value: createAt }]
    });
  }

  updateSuitabilityQuestions(agentId: string, suitability: Suitability) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personSuitabilityQuestions +
      '/' +
      agentId;
    return this.http.put(url, suitability);
  }

  updateSuitabilityScore(agentId: string, score: SuitabilityScore) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personSuitabilityScore +
      '/' +
      agentId;
    return this.http.put(url, score);
  }

  updateSuitabilityQuestionsTrack(agentId: string, questionsTrack: any) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personSuitabilityQuestionTrack +
      '/' +
      agentId;
    return this.http.put(url, questionsTrack);
  }

  updateFinancialStatement(id: string, financialStatement: FinancialStatement) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personByFinancialStatement;
    return this.http.put(url, {
      Id: id,
      FinancialStatement: financialStatement,
      UserId: get(this.userLogged, 'userID')
    });
  }

  getScoreByAgent(agentId: string): Observable<any> {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.personScoreByAgent +
      '/' +
      agentId;

    return this.http.get<any>(url);
  }

  uploadStockExchangeInvestors(
    file: FormData,
    userId: string
  ): Observable<UploadStockExchangeInvestorsOutput> {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.stockExchangeInvestorsUpload +
      userId;
    return this.http.post<UploadStockExchangeInvestorsOutput>(url, file);
  }

  downloadInvestorFile(file: Files): Observable<any> {
    if (file.fileType !== InvestorFileTypes.INVESTOR_REJECT_SUITABILITY_TERM
      && file.fileType !== InvestorFileTypes.INVESTOR_PAYMENT_ACCOUNT_OPENING_TERM) {
      const url =
        environment.apiCadastroSettings.url +
        environment.apiCadastroSettings.downloadInvestorDocumentUpload +
        file.fileName;
      return this.http.get<Blob>(url);
    }
    return this.msSignService
      .signTermUrl({ url: file.fileUrl })
      .pipe(
        switchMap((res) =>
          this.http.get(res.s3Url, { responseType: 'arraybuffer' })
        )
      );
  }

  sendNonConformityTerm(
    input: SendNonConformityTermInput
  ): Observable<{ message: string }> {
    const url = `${environment.apiCadastroSettings.url}${
      environment.apiCadastroSettings.personSendNonConformityTerm
    }/${get(this.userLogged, 'userID')}`;

    return this.http.post<{ message: string }>(url, input);
  }

  signsNonConformityTerm(input: SignNonConformityTermInput): Observable<{}> {
    const url = `${environment.apiCadastroSettings.url}${environment.apiCadastroSettings.personSignNonConformityTerm}`;

    return this.http.post(url, input);
  }

  generateSuitabilityFile(agentId: string, virtualRawFingerprint: string): Observable<any> {
    const url = environment.apiCadastroSettings.url + environment.apiCadastroSettings.personGenerateSuitabilityFile;

    return this.http.post(url, {agentId, virtualRawFingerprint, userId: get(this.userLogged, 'userID')});
  }
}
